import { lazy, type ComponentType } from 'react';

const CHUNK_RELOAD_KEY = 'route-chunk-reloaded';

/**
 * A hashed async chunk can 404 after a deploy while an old tab is still open.
 * Reload once to pick up the new manifest rather than rejecting into the
 * top-level `ErrorBoundary` and taking down the whole app; a repeat failure
 * falls through so we never loop.
 */
export function reloadOnceOnChunkError(error: unknown): Promise<never> {
  if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
    window.location.reload();
    return new Promise<never>(() => {});
  }

  throw error;
}

/**
 * `React.lazy` wrapper for a named export that survives post-deploy chunk 404s.
 * `load` is the raw dynamic `import()`; `exportName` is the component export to
 * pull out of the module.
 */
export function lazyChunk(
  load: () => Promise<Record<string, unknown>>,
  exportName: string
) {
  return lazy(() =>
    load()
      .then((module) => {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        return { default: module[exportName] as ComponentType };
      })
      .catch(reloadOnceOnChunkError)
  );
}
